/**
 * Consentement aux sollicitations commerciales.
 *
 * Le seul consentement e-mail globalement révocable, décision du 15/09/2026.
 * Tous les autres e-mails du produit sont soit dus (sécurité, obligation
 * légale de l'article L215-1, fil de support ouvert par l'utilisateur), soit
 * réglés là où ils se créent — les notifications de vote ont déjà
 * `polls.vote_notification_mode` par sondage, avec
 * `profiles.vote_notification_mode` comme défaut.
 *
 * Volontairement SÉPARÉ de `useDataControl`, qui gère la rétention et
 * `email_notifications`. Deux raisons : un consentement n'est pas un réglage
 * de confort, et il doit rester lisible isolément le jour où quelqu'un
 * demande « quand ai-je consenti ? ». D'où l'horodatage plutôt qu'un booléen.
 *
 * ⚠️ `profiles.email_notifications` n'est PAS ce consentement et ne doit pas
 * en tenir lieu : il vaut `true` par défaut sur tous les comptes, donc il ne
 * prouve rien. Il reste honoré là où il l'était déjà (trois RPC) et n'est pas
 * étendu — le retirer enverrait soudain des e-mails à des gens qui les avaient
 * coupés.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@core/lib/supabase";
import { useAuth } from "@core/contexts/AuthContext";
import { logger } from "@core/lib/logger";

export interface MarketingConsentState {
  /** `null` tant que l'état n'est pas connu — ne rien afficher d'ici là. */
  optedInAt: Date | null;
  isOptedIn: boolean;
  loading: boolean;
  saving: boolean;
  error: string | null;
  /** Donne ou retire le consentement. Résout à `true` si l'écriture a pris. */
  setConsent: (value: boolean) => Promise<boolean>;
}

export function useMarketingConsent(): MarketingConsentState {
  const { user } = useAuth();
  const [optedInAt, setOptedInAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setOptedInAt(null);
      setLoading(false);
      return;
    }
    let annule = false;
    void (async () => {
      const { data, error: err } = await supabase
        .from("profiles")
        .select("marketing_emails_opt_in_at")
        .eq("id", user.id)
        .maybeSingle();
      if (annule) return;
      if (err) {
        logger.warn("Marketing consent: read failed", "quota", err);
        setError("read_failed");
      } else {
        const v = (data as { marketing_emails_opt_in_at?: string | null } | null)
          ?.marketing_emails_opt_in_at;
        setOptedInAt(v ? new Date(v) : null);
      }
      setLoading(false);
    })();
    return () => {
      annule = true;
    };
  }, [user]);

  const setConsent = useCallback(
    async (value: boolean): Promise<boolean> => {
      if (!user) return false;
      setSaving(true);
      setError(null);
      // Retirer écrit NULL, et ne conserve pas la date : le RGPD demande de
      // pouvoir prouver un consentement, pas de garder trace d'un refus.
      const nouveau = value ? new Date().toISOString() : null;
      const { error: err } = await supabase
        .from("profiles")
        .update({ marketing_emails_opt_in_at: nouveau })
        .eq("id", user.id);
      setSaving(false);
      if (err) {
        logger.error("Marketing consent: write failed", "quota", err);
        setError("write_failed");
        return false;
      }
      setOptedInAt(nouveau ? new Date(nouveau) : null);
      return true;
    },
    [user],
  );

  return {
    optedInAt,
    isOptedIn: optedInAt !== null,
    loading,
    saving,
    error,
    setConsent,
  };
}
